import type { ChallengeDraft } from "@highmarch/app";
import { CHALLENGE_SEED_MAX, challengeTowerOrder, normalisedPool } from "@highmarch/domain";
import { btnHtml } from "../kit";
import { icon } from "../iconsKit";
import { kickerHtml } from "../components";
import { portraitUrl } from "../portraits";
import { toast } from "../toast";
import type { ChallengeSheetCtx } from "./challengeSheet";
import { rulesChipsHtml } from "./challengeWords";

/** The Create tab's state: the draft being made. */
export interface CreateState { readonly draft: ChallengeDraft }

/** A tower's face on its chip (CSS px). */
const TOWER_ART_PX = 34;

/** A random seed for "New seed" (an input of the code, like a typed one: never read by the sim but through the code). */
function randomSeed(): number { return Math.floor(Math.random() * (CHALLENGE_SEED_MAX + 1)); }

function towersHtml(ctx: ChallengeSheetCtx, d: ChallengeDraft): string {
  const B = ctx.catalog.balance, std = normalisedPool(B);
  return `<div class="chtowers">${challengeTowerOrder(B).map((a, bit) => {
    const t = B.towers[a], on = ((d.pool & ~d.banned) >> bit) & 1, art = portraitUrl(t.id), extra = !((std >> bit) & 1);
    const face = art ? `<img decoding="sync" src="${art}" alt="" width="${TOWER_ART_PX}" height="${TOWER_ART_PX}">` : icon("tower", 22);
    // a standard tower off is banned (a red ×); one outside the standard pool (the Beacon) off can be added (a gold +)
    const mark = on ? "" : extra ? `<i class="add disp">+</i>` : `<i class="ban">${icon("close", 14)}</i>`;
    return `<button type="button" class="chtower${on ? " on" : ""}${extra ? " extra" : ""}" data-bit="${bit}" aria-pressed="${on ? "true" : "false"}">${face}<small>${t.name}</small>${mark}</button>`;
  }).join("")}</div>`;
}

function twistsHtml(ctx: ChallengeSheetCtx, d: ChallengeDraft): string {
  const mods = ctx.catalog.balance.events.trial.modifiers, max = ctx.codes.data.maxModifiers;
  return `<div class="chtwists">${mods.map((m, i) => {
    const on = d.modifiers.includes(i), full = !on && d.modifiers.length >= max;
    return `<button type="button" class="chtwist${on ? " on" : ""}${full ? " full" : ""}" data-mod="${i}" aria-pressed="${on}"><b>${m.name}</b><small>${m.blurb}</small></button>`;
  }).join("")}</div>`;
}

function capsHtml(ctx: ChallengeSheetCtx, d: ChallengeDraft): string {
  return `<div class="chcaps">${ctx.codes.data.livesCaps.map((n) => `<button type="button" class="chcap${d.livesCap === n ? " on" : ""}" data-cap="${n}">${n === 0 ? "Full" : `${n} HP`}</button>`).join("")}</div>`;
}

/** The Create tab: level, seed, towers, twists, the Fortress cap, then the code with SHARE (the gold) and PLAY. */
export function createTabHtml(ctx: ChallengeSheetCtx, s: CreateState): string {
  const d = s.draft, levels = ctx.codes.levels(), B = ctx.catalog.balance;
  if (levels.length === 0) return `<p class="chmsg">${icon("lock", 14)}<span>Clear level ${ctx.codes.data.levelFrom} to make a code.</span></p>`;
  const i = levels.indexOf(d.level), r = ctx.codes.make(d), today = d.seed === ctx.codes.daySeed(ctx.now());
  const code = r.ok
    ? `<section class="chcodebox"><small class="disp">YOUR CODE</small><b class="disp chcode">${r.code}</b>${rulesChipsHtml(ctx.catalog, r.challenge)}</section>`
    : `<p class="chmsg bad">${icon("close", 14)}<span>${r.message}</span></p>`;
  return `${kickerHtml("LEVEL", "one you have cleared")}
    <div class="chstep"><button type="button" class="chstepbtn prev" aria-label="Higher level"${i <= 0 ? " disabled" : ""}>${icon("back", 18)}</button>
      <div class="chstepv"><b class="disp">Level ${d.level}</b><small>${B.level(d.level).name}</small></div>
      <button type="button" class="chstepbtn next" aria-label="Lower level"${i >= levels.length - 1 ? " disabled" : ""}>${icon("chev", 18)}</button></div>
    ${kickerHtml("THE DRAW", `#${d.seed}`)}
    <div class="chcaps seed"><button type="button" class="chcap${today ? " on" : ""}" data-seed="day">Today's</button><button type="button" class="chcap${today ? "" : " on"}" data-seed="new">${icon("rotate", 14)} New draw</button></div>
    ${kickerHtml("TOWERS", "tap to ban or add")}${towersHtml(ctx, d)}
    ${kickerHtml("TWISTS", `up to ${ctx.codes.data.maxModifiers}`)}${twistsHtml(ctx, d)}
    ${kickerHtml("FORTRESS HEALTH")}${capsHtml(ctx, d)}
    ${code}
    <div class="chacts">${ctx.share ? btnHtml("SHARE", "gold", { icon: "star", className: "disp chshare", disabled: !r.ok }) : ""}${btnHtml("PLAY", ctx.share ? "blue" : "gold", { icon: "play", className: "disp chplaymade", disabled: !r.ok })}</div>`;
}

/** Wires the Create tab: every pick redraws through `set`; SHARE and PLAY act on the made code. */
export function bindCreateTab(sheet: HTMLElement, ctx: ChallengeSheetCtx, s: CreateState, set: (s: CreateState) => void, close: () => void): void {
  const d = s.draft, levels = ctx.codes.levels(), std = normalisedPool(ctx.catalog.balance);
  const put = (p: Partial<ChallengeDraft>): void => set({ draft: { ...d, ...p } });
  const step = (by: number) => { const i = levels.indexOf(d.level) + by; if (i >= 0 && i < levels.length) put({ level: levels[i] }); };
  sheet.querySelector<HTMLButtonElement>(".chstepbtn.prev")?.addEventListener("click", () => step(-1));
  sheet.querySelector<HTMLButtonElement>(".chstepbtn.next")?.addEventListener("click", () => step(1));
  for (const b of sheet.querySelectorAll<HTMLButtonElement>("[data-seed]")) b.onclick = () => put({ seed: b.dataset.seed === "day" ? ctx.codes.daySeed(ctx.now()) : randomSeed() });
  for (const b of sheet.querySelectorAll<HTMLButtonElement>(".chtower")) b.onclick = () => {
    const bit = 1 << Number(b.dataset.bit);
    // a standard tower is banned or unbanned; one outside the standard pool (the Beacon) joins or leaves the pool
    if (std & bit) put({ pool: d.pool | bit, banned: d.banned ^ bit });
    else put({ pool: d.pool ^ bit, banned: d.banned & ~bit });
  };
  for (const b of sheet.querySelectorAll<HTMLButtonElement>(".chtwist")) b.onclick = () => {
    const m = Number(b.dataset.mod), on = d.modifiers.includes(m);
    if (!on && d.modifiers.length >= ctx.codes.data.maxModifiers) { toast(sheet, `Up to ${ctx.codes.data.maxModifiers} twists: tap one to drop it`); return; }
    put({ modifiers: on ? d.modifiers.filter((x) => x !== m) : [...d.modifiers, m] });
  };
  for (const b of sheet.querySelectorAll<HTMLButtonElement>(".chcap[data-cap]")) b.onclick = () => put({ livesCap: Number(b.dataset.cap) });
  const made = () => { const r = ctx.codes.make(d); return r.ok ? r : null; };
  sheet.querySelector<HTMLButtonElement>(".chshare")?.addEventListener("click", () => { const r = made(); if (r) ctx.share?.(r.code, r.challenge); });
  sheet.querySelector<HTMLButtonElement>(".chplaymade")?.addEventListener("click", () => { const r = made(); if (r) { close(); ctx.start(r.challenge); } });
}
